import { useState } from "react";
import { useCallback } from "react";
import useApi from "./useApi";
import useFilterV2 from "./useFilter.v2";
import useTable from "./useTable";
import useMountAsync from "./useMountAsync";

const useDataTable = ({ filter, table, api, searchKey = "", value }) => {
  const [search, setSearch] = useState(searchKey);
  const [requestParams, setRequestParams] = useState();

  const [isLastPage, setIsLastPage] = useState(false);

  const {
    filterState,
    filterCount,
    mappedFilterState,
    modifyFilter,
    clearFilter,
    submitFilter,
    resetFilter,
  } = useFilterV2({
    ...filter,
    initialState: filter.initialState,
    mapper: filter.mapper,
  });

  const { key, hasColumnPicker, columns, mapper } = table;

  const {
    request,
    loading,
    result = { [key]: value || [], count: 0 },
  } = useApi({ api: api?.api, params: api?.params });

  const { pageState, modifyPage, data, isReverse } = useTable({
    hasColumnPicker,
    columns,
    mapper,
    data: result?.[key] || value || [],
    count: result?.count || 0,
  });

  const fetch = useCallback(
    async ({
      newPageState: newPageStateValue,
      newSearchKey,
      newFilterState,
      retry,
      apiOptions,
    }) => {
      const { isReverse: isReverseStateValue, ...newPageState } = { ...newPageStateValue };
      let requestParams = {
        ...pageState,
        ...mappedFilterState,
        searchKey: search,
      };

      if (newPageState) {
        requestParams = { ...requestParams, ...newPageState };
      }
      if (newSearchKey || newSearchKey === "") {
        requestParams = { ...requestParams, searchKey: newSearchKey };
      }
      if (newFilterState) {
        requestParams = { ...requestParams, ...newFilterState };
      }
      setRequestParams(requestParams);
      const p = api?.mapper ? api?.mapper(requestParams) : requestParams;

      const isApiEmpty = !api;

      const res = !isApiEmpty
        ? await request(
            {
              ...p,
              order: isReverseStateValue ? "asc" : "desc",
            },
            retry,
            apiOptions
          )
        : {
            [key]: value,
          };

      if (res && requestParams.page !== 1) {
        const resultData = res[key] || [];
        const returnToPage1 =
          isReverseStateValue &&
          (resultData.length !== requestParams.perPage || !resultData.length);
        const emptyLastPage = !isReverseStateValue && !resultData.length;
        const newPage = returnToPage1
          ? 1
          : emptyLastPage
          ? requestParams.page - 1
          : requestParams.page;

        if (returnToPage1 || emptyLastPage) {
          const isReverseValue = returnToPage1 ? false : isReverseStateValue;
          modifyPage({
            page: newPage,
            perPage: requestParams.perPage,
            isReverse: returnToPage1 ? isReverseValue : isReverseStateValue,
          });
          if (emptyLastPage) {
            setIsLastPage(true);
          }
          request(
            { ...p, page: newPage, order: isReverseValue ? "asc" : "desc" },
            retry,
            apiOptions
          );
        }
      }
    },
    [request, pageState, mappedFilterState, search, key, modifyPage, api, value]
  );

  useMountAsync(() => {
    fetch({ page: 1, newSearchKey: filter.initialState?.searchKey || undefined });
  });

  const refetch = useCallback(() => {
    fetch({ newPageState: modifyPage(pageState) });
  }, [modifyPage, pageState, fetch]);

  const applyPage = useCallback(
    ({ page, perPage, isReverse }) => {
      fetch({ newPageState: modifyPage({ page, perPage, isReverse }) });
    },
    [fetch, modifyPage]
  );

  const modifySearch = useCallback((searchKey) => {
    setSearch(searchKey);
  }, []);

  const applySearch = useCallback(() => {
    fetch({ newPageState: modifyPage({ page: 1, isReverse: false }) });
  }, [fetch, modifyPage]);

  const applyClearSearch = useCallback(() => {
    setSearch("");
    fetch({ newPageState: modifyPage({ page: 1, isReverse: false }), newSearchKey: "" });
  }, [fetch, modifyPage]);

  const applyFilter = useCallback(() => {
    fetch({ newPageState: modifyPage({ page: 1, isReverse: false }) });
    submitFilter();
  }, [fetch, modifyPage, submitFilter]);

  const applyClearFilter = useCallback(
    (callback) => {
      callback?.();
      clearFilter();
      fetch({
        newPageState: modifyPage({ page: 1, perPage: 10, isReverse: false }),
        newFilterState: filter.mapper(modifyFilter(filter.initialState)),
      });
    },
    [fetch, modifyPage, modifyFilter, filter, clearFilter]
  );

  return {
    filter: {
      filterState,
      filterCount,
      modifyFilter,
      mappedFilterState,
      applyFilter,
      applyClearFilter,
      resetFilter,
    },
    search: { searchKey: search, modifySearch, applySearch, applyClearSearch },
    table: {
      loading,
      result,
      pageState,
      dataKey: key,
      data,
      columns,
      applyPage,
      requestParams,
      fetch,
      isReverse,
      isLastPage,
      setIsLastPage,
      refetch,
    },
  };
};

export default useDataTable;
